/**
 * CSS-строка для левой колонки (рельс спейсов + ряд вкладок + список чатов).
 *
 * ПОЧЕМУ здесь CSS, а не inline-объекты, как во всём остальном коде:
 * inline-стиль не умеет :hover, а наведение в списке чатов и на иконках
 * рельса обязательно. Альтернатива — держать hovered-элемент в state React,
 * но тогда каждое движение мыши перерисовывает весь список (на стенде это
 * 100+ строк). Одна статическая таблица стилей дешевле и не зависит от
 * числа комнат.
 *
 * ВСЕ цвета и размеры подставляются из `theme.ts`. Литеральных hex-строк
 * здесь быть не должно — иначе получаются два источника правды и будущая
 * лента разъезжается с колонкой по цвету (ровно эта проблема и была).
 *
 * Класс с префиксом alg- , чтобы не столкнуться ни с чем чужим.
 */
import { color, font, radius, size } from "./theme";

export const roomListCss = `
.alg-room-row {
  display: flex;
  gap: 10px;
  align-items: center;
  width: 100%;
  height: ${size.rowHeight}px;
  box-sizing: border-box;
  padding: 0 8px 0 10px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;
  border-radius: ${radius.md}px;
}
.alg-room-row:hover { background: ${color.hover}; }
/* АКТИВНАЯ КОМНАТА — ПРАВКА ПО АНТОНУ 16:54 п.3: «выделение комнаты в
   списке не насыщенным красным, а гармоничным красноватым оттенком, чтоб
   всё легко читалось».

   Было: заливка полным акцентом + белый текст поверх. Читаемость страдала
   дважды — насыщенный красный кричит в списке, а превью и время белым по
   красному теряют свою иерархию (всё становится одинаково белым).

   Стало: мягкий красноватый фон того же тона (accentSoft) + ТЁМНЫЙ текст,
   как в неактивных строках, плюс красная полоса слева как признак выбора.
   Иерархия «имя тёмное / превью приглушённое» сохраняется, акцент
   обозначен, глаз не режет. */
.alg-room-row.is-active {
  background: ${color.accentSoft};
  /* Полоса-указатель вместо заливки акцентом: сам акцент остаётся ярким,
     но занимает 3px, а не всю строку. */
  box-shadow: inset 3px 0 0 ${color.accent};
}
.alg-room-row.is-active:hover { background: ${color.accentSoftHover}; }
.alg-room-row.is-active .alg-room-name { color: ${color.text}; }
.alg-room-row.is-active .alg-room-preview,
.alg-room-row.is-active .alg-room-time,
.alg-room-row.is-active .alg-room-sender { color: ${color.textDim}; }
.alg-room-row.is-active .alg-room-badge { background: ${color.accent}; color: ${color.textOnAccent}; }
/* Значок упоминания остаётся красным и в активной строке (12.10): иначе он
   сливается с акцентной заливкой и упоминание «пропадает». */
.alg-room-row.is-active .alg-room-badge.is-mention { background: ${color.badgeHighlight}; color: ${color.textOnAccent}; }

/* ===== СУЖЕННАЯ ЛЕВАЯ КОЛОНКА — ТОЛЬКО АВАТАРЫ (Антон 2026-09-18, п.8) =====

   Строка сжимается до квадрата с одним аватаром по центру. Высота меньше
   обычной (аватар + воздух вместо двух этажей текста): смысл режима в том,
   чтобы видеть больше чатов сразу, а не только освободить ширину.
   Подсветка активной — та же мягкая заливка + полоса слева (правило Антона
   16:54 п.3 действует и здесь). */
.alg-room-row.is-collapsed {
  justify-content: center;
  gap: 0;
  height: auto;
  padding: 4px 0;
}

/* Бейдж поверх аватара: в свёрнутом виде строки с цифрой справа нет,
   и без наложения непрочитанное исчезло бы из вида совсем. */
.alg-collapsed-badge {
  position: absolute;
  right: -2px;
  bottom: -2px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  box-sizing: border-box;
  border-radius: 8px;
  /* Тот же цвет, что у обычного бейджа в развёрнутой строке (s.badge). */
  background: ${color.tgBlue};
  color: ${color.textOnAccent};
  font-size: ${font.tiny}px;
  font-weight: ${font.weightBold};
  line-height: 16px;
  text-align: center;
  /* Кайма цветом панели: бейдж наезжает на аватар, и без каймы цвета
     сливаются в пятно. Тот же приём, что у аватаров читателей в ленте. */
  box-shadow: 0 0 0 2px ${color.sidebarBg};
  pointer-events: none;
}

/* «Есть новое, но не мне»: в развёрнутом виде это жирный текст строки,
   здесь текста нет — значит точка. */
.alg-collapsed-dot {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${color.tgBlue};
  box-shadow: 0 0 0 2px ${color.sidebarBg};
  pointer-events: none;
}

/* Кнопка сворачивания/разворачивания — на верхней границе колонки справа.
   Абсолютом, чтобы не входить в поток шапки (её в свёрнутом виде нет вовсе)
   и чтобы положение кнопки не менялось между режимами. */
.alg-collapse-toggle {
  position: absolute;
  top: 6px;
  right: 6px;
  z-index: 6;
  width: 24px;
  height: 24px;
  display: grid;
  place-items: center;
  padding: 0;
  border: none;
  border-radius: ${radius.sm}px;
  background: transparent;
  color: ${color.textMuted};
  cursor: pointer;
  opacity: 0.55;
  transition: opacity 120ms, background 120ms;
}
.alg-collapse-toggle:hover { opacity: 1; background: ${color.hover}; }
.alg-collapse-toggle:focus-visible { opacity: 1; outline: 2px solid ${color.accent}; outline-offset: 1px; }
/* В свёрнутом виде кнопка — единственный способ вернуть колонку, поэтому
   она всегда видна, а не проявляется по наведению. Спрятанная кнопка
   выхода из режима — ловушка, а не минимализм. */
.alg-room-sidebar.is-collapsed .alg-collapse-toggle {
  position: static;
  opacity: 0.8;
  margin: 6px auto 2px;
}
.alg-room-sidebar.is-collapsed { padding: 0; }

/* ГОРИЗОНТАЛЬНЫЙ ряд вкладок (уточнение Антона 2026-09-09 18:14 UTC).
   Три свойства ниже решают ровно три требования к ряду и убирать их нельзя:
     flex-shrink: 0   — вкладка НЕ сжимается до нечитаемого; без него flex по умолчанию
                        утрамбовывает 9 вкладок в ширину колонки и названия превращаются в кашу;
     white-space: nowrap — название не ломается на две строки;
     родительский flex-wrap: nowrap + overflow-x — ряд не едет на вторую строку, а прокручивается. */
.alg-tabs {
  scrollbar-width: none;            /* Firefox: полоса скрыта, как в Telegram */
  -ms-overflow-style: none;
}
.alg-tabs::-webkit-scrollbar { height: 0; display: none; }  /* Chromium/WebKit */

.alg-tab {
  position: relative;
  flex: 0 0 auto;
  border: none;
  background: transparent;
  font: inherit;
  font-size: ${font.small}px;
  font-weight: ${font.weightNormal};
  color: ${color.textMuted};
  padding: 9px 12px 8px;
  cursor: pointer;
  white-space: nowrap;
  border-radius: ${radius.sm}px ${radius.sm}px 0 0;
  max-width: ${size.tabMaxWidth}px;  /* очень длинное имя спейса обрезаем, а не растягиваем ряд */
  overflow: hidden;
  text-overflow: ellipsis;
  /* Safari on iPad otherwise treats a short tap in this horizontal
     overflow region as the beginning of a scroll gesture often enough to
     suppress the synthetic click. Keep horizontal panning on the parent,
     while making an intentional tap on a native button activate normally. */
  touch-action: manipulation;
}
.alg-tab:hover { background: ${color.tabHover}; }
.alg-tab.is-active { color: ${color.tgBlue}; font-weight: ${font.weightBold}; }
.alg-tab.is-active::after {
  content: "";
  position: absolute;
  left: 10px; right: 10px; bottom: 0;
  height: 3px;
  border-radius: 3px 3px 0 0;
  background: ${color.tgBlue};
}

.alg-tab-count { background: ${color.badgeMuted}; }
.alg-tab.is-active .alg-tab-count { background: ${color.tgBlue}; }

.alg-search-input::placeholder { color: ${color.placeholder}; }
.alg-search-input:focus { outline: none; border-color: ${color.tgBlue}; background: ${color.bg}; }

/* Строка результата поиска по содержимому. Подсветка та же, что у строки
   чата, но высота НЕ фиксирована: фрагмент текста занимает до двух строк. */
.alg-search-hit:hover { background: ${color.hover}; }
.alg-search-hit:focus-visible { outline: 2px solid ${color.tgBlue}; outline-offset: -2px; }

/* Compact Outlook-style chat filter next to search. The menu stays above
   the room scroller and uses native buttons for Enter/Space activation. */
.alg-filter-button {
  position: relative;
  width: 34px;
  height: 32px;
  padding: 0;
  display: grid;
  place-items: center;
  border: 1px solid ${color.border};
  border-radius: ${radius.circle};
  background: ${color.bg};
  color: ${color.textDim};
  cursor: pointer;
}
/* Чип «Меня ждут» над списком (Антон 12.10). Одна строка, без переноса:
   самый длинный текст («Esperando por mim» + число) влезает в 360px. */
.alg-waiting-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  height: 28px;
  padding: 0 6px 0 12px;
  border: 1px solid ${color.border};
  border-radius: 14px;
  background: ${color.bg};
  color: ${color.text};
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
}
.alg-waiting-chip-label { overflow: hidden; text-overflow: ellipsis; }
.alg-waiting-chip-count {
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: ${color.accent};
  color: #fff;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
}
.alg-waiting-chip:hover { background: ${color.hover}; }
.alg-waiting-chip.is-active {
  border-color: ${color.accent};
  background: ${color.accentSoft};
}
.alg-waiting-chip.is-empty:not(.is-active) { color: ${color.textDim}; font-weight: 500; }
.alg-waiting-chip.is-empty .alg-waiting-chip-count { background: ${color.border}; color: ${color.textDim}; }
.alg-waiting-chip:focus-visible { outline: 2px solid ${color.tgBlue}; outline-offset: 2px; }
.alg-filter-button:hover { background: ${color.hover}; color: ${color.text}; }
.alg-filter-button.is-active {
  color: ${color.tgBlue};
  border-color: ${color.tgBlue};
  background: ${color.accentSoft};
}
.alg-filter-button:focus-visible,
.alg-filter-item:focus-visible,
.alg-filter-clear:focus-visible {
  outline: 2px solid ${color.tgBlue};
  outline-offset: 2px;
}
.alg-filter-dot {
  position: absolute;
  right: 4px;
  top: 4px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${color.tgBlue};
}
.alg-filter-menu {
  position: absolute;
  right: 0;
  top: calc(100% + 5px);
  z-index: 65;
  min-width: 190px;
  box-sizing: border-box;
  padding: 5px;
  border: 1px solid ${color.border};
  border-radius: ${radius.sm}px;
  background: ${color.bg};
  box-shadow: 0 8px 24px ${color.backdrop};
}
.alg-filter-item,
.alg-filter-clear {
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 10px;
  border: none;
  border-radius: ${radius.sm}px;
  background: transparent;
  color: ${color.text};
  font: inherit;
  font-size: ${font.small}px;
  text-align: left;
  cursor: pointer;
}
.alg-filter-item:hover,
.alg-filter-clear:hover { background: ${color.hover}; }
.alg-filter-item.is-active { color: ${color.tgBlue}; font-weight: ${font.weightBold}; }
.alg-filter-clear {
  margin-top: 4px;
  border-top: 1px solid ${color.border};
  border-radius: 0 0 ${radius.sm}px ${radius.sm}px;
  color: ${color.textDim};
}

/* ВЕРТИКАЛЬНЫЙ РЕЛЬС ПРОСТРАНСТВ (ориентир — Element classic).
   Полосу прокрутки прячем: рельс узкий, полоса съела бы треть его ширины
   и иконки перестали бы быть круглыми на вид. */
.alg-rail {
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.alg-rail::-webkit-scrollbar { width: 0; display: none; }

.alg-rail-btn {
  position: relative;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  font: inherit;
  flex: 0 0 auto;
  display: block;
  touch-action: manipulation;
  /* Активный спейс отмечаем вертикальной риской СЛЕВА (::before), а не
     заливкой: заливка круглой иконки конфликтовала бы с цветом аватара. */
}
.alg-rail-btn::before {
  content: "";
  position: absolute;
  left: -8px;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 0;
  border-radius: 0 3px 3px 0;
  background: ${color.tgBlue};
  transition: height 90ms ease-out;
}
.alg-rail-btn:hover::before { height: 12px; }
.alg-rail-btn.is-active::before { height: ${size.railIcon - 12}px; }
.alg-rail-btn:hover .alg-rail-icon { filter: brightness(0.94); }
/* НЕ АПСКЕЙЛИТЬ МАЛЕНЬКИЙ THUMBNAIL CSS-ом (Антон 2026-09-18, п.9 дословно).
   Размер теперь запрашивается с запасом (см. useAuthedAvatar/avatarThumbPx),
   поэтому картинка всегда СЖИМАЕТСЯ, а не растягивается. image-rendering сюда
   СОЗНАТЕЛЬНО НЕ ДОБАВЛЕН: дефолтный «auto» у браузеров и есть качественное
   сглаживание, а «-webkit-optimize-contrast» в Chrome исторически ведёт себя как
   nearest-neighbour и сделал бы хуже ровно в том случае, который мы чиним. */
.alg-rail-btn.is-active .alg-rail-icon {
  box-shadow: 0 0 0 2px ${color.railBg}, 0 0 0 4px ${color.tgBlue};
}

/* Preserve full desktop behavior while preventing the fixed navigation
   columns from overflowing above the room pane on narrower WebKit views. */
@media (max-width: 1600px) {
  /* :not([data-collapsed="1"]) обязателен (п.8, 2026-09-18): эти правила с
     !important иначе перебивают ширину свёрнутой колонки, и на экране
     уже 1600px кнопка сворачивания переставала бы что-либо делать. */

  /* ШИРИНА БОЛЬШЕ НЕ ФИКСИРУЕТСЯ (Антон 2026-10-05): раньше здесь стояли
     flex-basis: 320px на обёртке и 262px !important на самой колонке.
     Это был НАСТОЯЩИЙ потолок ресайза: правило с !important перебивало
     inline-ширину, которую выставляет ручка перетаскивания, и на любом
     окне ≤1600px (почти на всех) колонка визуально замирала на 262px,
     что бы ни делал пользователь. Теперь ширину держит только inline
     (usePanelWidth: min 220, max — доля окна), а здесь остаются
     min-width: 0 — чтобы flex-элемент мог сжиматься и не вылезать
     поверх ленты на узких WebKit-вьюпортах, ради чего правило и писалось. */
  .alg-room-list-shell:not([data-collapsed="1"]) {
    min-width: 0 !important;
  }
  .alg-room-sidebar:not(.is-collapsed) {
    min-width: 0 !important;
  }
  .alg-room-header {
    gap: 8px !important;
    padding-left: 10px !important;
    padding-right: 10px !important;
  }
  .alg-room-header-actions {
    flex: 0 0 auto !important;
    flex-wrap: nowrap !important;
    gap: 2px !important;
  }
  .alg-room-header-avatar {
    width: 34px !important;
    height: 34px !important;
    flex-basis: 34px !important;
  }
  .alg-room-header-subtitle {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

`;
